import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
  Pagination,
  Stack,
} from '@mui/material';
import { BaseTableList } from 'components/AgGrid/AgGrid';
import { useAppSelector } from 'store/hook';
import { selectTireInstallation } from 'store/tire-installation.slice';
import TireInstallationHandler from 'handlers/tire-installation.handler';
import { isDarkTheme } from 'utils/theme';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { type ColDef } from 'ag-grid-community';
import { type TireInstallation } from 'types/tire-installation.types';
import { ThemePalette } from 'mui.theme';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { FactCheck, Visibility } from '@mui/icons-material';
import { ViewInspectionsModal } from './components/ViewInspectionsModal';
import { CreateInspectionModal } from './components/CreateInspectionModal';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const TireInspection = () => {
  const { t } = useTranslation();
  const theme = useAppSelector((state) => state.authReducer.customer.theme);
  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const enableDarkTheme = isDarkTheme(theme);
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const { tireInstallations, isLoading, total, totalPages } = useAppSelector(
    selectTireInstallation
  );
  const assets = useAppSelector((state) => state.assetReducer.assets);

  const [selectedAssetId, setSelectedAssetId] = useState<string>('');
  const [selectedPosition, setSelectedPosition] = useState<string>('');
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedInstallation, setSelectedInstallation] =
    useState<TireInstallation | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const tireInstallationHandler = new TireInstallationHandler();

  // Static list of common positions to support server-side pagination correctly
  const uniquePositions = [
    'Left Front',
    'Right Front',
    'Left Rear',
    'Right Rear',
    'LF',
    'RF',
    'LR',
    'RR',
    'LFI',
    'LFO',
    'RFI',
    'RFO',
    'LRI',
    'LRO',
    'RRI',
    'RRO',
  ];

  useEffect(() => {
    void tireInstallationHandler.getActiveTireInstallations({
      customerId,
      assetId: selectedAssetId || undefined,
      position: selectedPosition || undefined,
      page: currentPage,
      limit: pageSize,
    });
  }, [customerId, selectedAssetId, selectedPosition, currentPage, pageSize]);

  const handleAssetChange = (event: any) => {
    setSelectedAssetId(event.target.value as string);
    setCurrentPage(1);
  };

  const handlePositionChange = (event: any) => {
    setSelectedPosition(event.target.value as string);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSelectedAssetId('');
    setSelectedPosition('');
    setCurrentPage(1);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handleCreateInspection = (installation: TireInstallation) => {
    setSelectedInstallation(installation);
    setIsCreateModalOpen(true);
  };

  const handleViewAllInspections = (installation: TireInstallation) => {
    setSelectedInstallation(installation);
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedInstallation(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setSelectedInstallation(null);
  };

  const handleCreateSuccess = async () => {
    // Refresh the tire installations list after creating a new inspection
    await tireInstallationHandler.getActiveTireInstallations({
      customerId,
      assetId: selectedAssetId || undefined,
      position: selectedPosition || undefined,
      page: currentPage,
      limit: pageSize,
    });
  };

  // Date formatter
  const DateRenderer = (props: any) => {
    if (!props.value) return <span>--</span>;
    return <span>{convertStdDateString(props.value, 'MMM DD, YYYY')}</span>;
  };

  // Asset Name renderer
  const AssetNameRenderer = (props: any) => {
    const assetId = props.value;
    const asset = assets.find((a) => a.assetId === assetId);
    return <span>{asset ? asset.bumperNumber : 'Unknown Asset'}</span>;
  };

  // Tire Condition renderer
  const TireConditionRenderer = (props: any) => {
    const { t } = useTranslation();
    const latestLog = props.data.latestPerformanceLog;
    if (!latestLog) return <span style={{ opacity: 0.5 }}>{t('no_data')}</span>;

    const condition = latestLog.currentTireCondition;
    const getConditionColor = (condition: string) => {
      switch (condition) {
        case 'GOOD':
          return 'success';
        case 'WATCH':
          return 'warning';
        case 'REPAIRABLE':
          return 'warning';
        case 'SCRAP':
          return 'error';
        default:
          return 'default';
      }
    };

    return (
      <Chip
        label={condition}
        color={getConditionColor(condition)}
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  // Air Pressure renderer
  const AirPressureRenderer = (props: any) => {
    const latestLog = props.data.latestPerformanceLog;
    if (!latestLog?.measuredAirPressure)
      return <span style={{ opacity: 0.5 }}>--</span>;
    return <span>{latestLog.measuredAirPressure} PSI</span>;
  };

  // Tread Depth renderer
  const TreadDepthRenderer = (props: any) => {
    const latestLog = props.data.latestPerformanceLog;
    if (!latestLog?.averageTreadDepth)
      return <span style={{ opacity: 0.5 }}>--</span>;
    return <span>{latestLog.averageTreadDepth} mm</span>;
  };

  // Last Inspection Date renderer
  const LastInspectionRenderer = (props: any) => {
    const { t } = useTranslation();
    const latestLog = props.data.latestPerformanceLog;
    if (!latestLog?.timestamp)
      return <span style={{ opacity: 0.5 }}>{t('no_inspection')}</span>;
    return (
      <span>{convertStdDateString(latestLog.timestamp, 'MMM DD, YYYY')}</span>
    );
  };

  // Damage Reported renderer
  const DamageReportedRenderer = (props: any) => {
    const latestLog = props.data.latestPerformanceLog;
    if (!latestLog) return <span style={{ opacity: 0.5 }}>--</span>;

    const isDamaged = latestLog.damageReported;
    return (
      <Chip
        label={isDamaged ? 'Yes' : 'No'}
        color={isDamaged ? 'error' : 'success'}
        size="small"
        variant="outlined"
      />
    );
  };

  // Actions renderer
  const ActionsRenderer = (props: any) => {
    const { t } = useTranslation();
    const installation = props.data as TireInstallation;

    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
        <Tooltip title={t('create_new_inspection')}>
          <IconButton
            size="small"
            onClick={() => {
              handleCreateInspection(installation);
            }}
            sx={{
              color: ThemePalette.typography.primary,
              '&:hover': {
                backgroundColor: enableDarkTheme
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <FactCheck fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('view_all_inspections')}>
          <IconButton
            size="small"
            onClick={() => {
              handleViewAllInspections(installation);
            }}
            sx={{
              color: ThemePalette.typography.primary,
              '&:hover': {
                backgroundColor: enableDarkTheme
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // Column definitions
  const columnDefs: Array<ColDef<TireInstallation>> = [
    {
      field: 'tireSerialNumber',
      headerName: 'Serial Number',
      minWidth: 180,
      cellStyle: {
        fontWeight: 600,
        color: enableDarkTheme
          ? ThemePalette.typography.white
          : ThemePalette.typography.primary,
      },
    },
    {
      field: 'assetId',
      headerName: t('asset'),
      minWidth: 150,
      cellRenderer: AssetNameRenderer,
    },
    {
      field: 'position',
      headerName: t('boundary_importer_field_pos'),
      minWidth: 140,
    },
    {
      field: 'installationDate',
      headerName: t('installation_date_2'),
      minWidth: 160,
      cellRenderer: DateRenderer,
    },
    {
      field: 'odometerAtInstall',
      headerName: t('odometer_install'),
      minWidth: 160,
    },
    {
      field: 'engineHoursAtInstall',
      headerName: t('engine_hours_install'),
      minWidth: 180,
    },
    {
      field: 'latestPerformanceLog',
      headerName: t('condition'),
      minWidth: 130,
      cellRenderer: TireConditionRenderer,
    },
    {
      field: 'latestPerformanceLog',
      headerName: t('air_pressure'),
      minWidth: 140,
      cellRenderer: AirPressureRenderer,
    },
    {
      field: 'latestPerformanceLog',
      headerName: t('tread_depth'),
      minWidth: 140,
      cellRenderer: TreadDepthRenderer,
    },
    {
      field: 'latestPerformanceLog',
      headerName: t('damage_reported'),
      minWidth: 160,
      cellRenderer: DamageReportedRenderer,
    },
    {
      field: 'latestPerformanceLog',
      headerName: t('last_inspection'),
      minWidth: 160,
      cellRenderer: LastInspectionRenderer,
    },
    {
      headerName: t('actions'),
      minWidth: 120,
      maxWidth: 120,
      pinned: 'right',
      cellRenderer: ActionsRenderer,
      sortable: false,
      filter: false,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
  ];

  return (
    <CustomMuiThemeProvider>
      <Box sx={{ padding: '20px' }}>
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: '250px' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                marginBottom: '8px',
              }}
            >
              {t('tire_inspection')}
            </Typography>
            <Typography variant="body2">
              {isLoading ? (
                'Loading...'
              ) : (
                <>
                  Showing {tireInstallations.length} of {total}{' '}
                  {t('active_installation')}
                  {total !== 1 ? 's' : ''}
                  {(selectedAssetId || selectedPosition) && ' (Filtered)'}
                </>
              )}
            </Typography>
          </Box>

          {/* Filters */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {/* Asset Filter */}
            <FormControl
              size="small"
              sx={{
                minWidth: 200,
              }}
            >
              <InputLabel>{t('filter_by_asset')}</InputLabel>
              <Select
                value={selectedAssetId}
                label={t('filter_by_asset')}
                onChange={handleAssetChange}
                sx={{
                  color: palette.textPrimary,
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: enableDarkTheme
                      ? ThemePalette.typography.grey
                      : palette.borderGrey,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: enableDarkTheme
                      ? ThemePalette.typography.white
                      : ThemePalette.typography.primary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: ThemePalette.typography.primary,
                  },
                }}
              >
                <MenuItem value="">{t('all_assets')}</MenuItem>
                {assets.map((asset) => (
                  <MenuItem key={asset.assetId} value={asset.assetId}>
                    {asset.bumperNumber}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Position Filter */}
            <FormControl
              size="small"
              sx={{
                minWidth: 180,
              }}
            >
              <InputLabel>{t('filter_by_position')}</InputLabel>
              <Select
                value={selectedPosition}
                label={t('filter_by_position')}
                onChange={handlePositionChange}
                sx={{
                  color: palette.textPrimary,
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: enableDarkTheme
                      ? ThemePalette.typography.grey
                      : palette.borderGrey,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: enableDarkTheme
                      ? ThemePalette.typography.white
                      : ThemePalette.typography.primary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: ThemePalette.typography.primary,
                  },
                }}
              >
                <MenuItem value="">{t('all_positions')}</MenuItem>
                {uniquePositions.map((position) => (
                  <MenuItem key={position} value={position}>
                    {position}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Clear Filters Button */}
            {(selectedAssetId || selectedPosition) && (
              <Typography
                variant="body2"
                onClick={handleClearFilters}
                sx={{
                  cursor: 'pointer',
                  color: ThemePalette.typography.primary,
                  textDecoration: 'underline',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                {t('clear_filters')}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Table Section */}
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : tireInstallations.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
              backgroundColor: enableDarkTheme
                ? palette.boxBackground
                : palette.boxBackground,
              borderRadius: '8px',
              border: `1px dashed ${
                enableDarkTheme
                  ? ThemePalette.typography.grey
                  : ThemePalette.typography.darkGrey
              }`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: enableDarkTheme
                  ? ThemePalette.typography.grey
                  : ThemePalette.typography.darkGrey,
                marginBottom: '8px',
              }}
            >
              {t('no_active_tire_installations_found')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: enableDarkTheme
                  ? ThemePalette.typography.grey
                  : ThemePalette.typography.darkGrey,
              }}
            >
              {selectedAssetId || selectedPosition
                ? 'No installations match the selected filters.'
                : 'There are no active tire installations at this time.'}
            </Typography>
          </Box>
        ) : (
          <>
            <BaseTableList
              rowData={tireInstallations}
              columnDefs={columnDefs}
              pagination={false}
              tableStyles={{
                height: `55vh`,
                borderRadius: '8px',
              }}
              getRowId={(params: any) => params.data.installationId}
            />

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Stack
                spacing={2}
                sx={{
                  marginTop: '20px',
                  alignItems: 'center',
                  paddingBottom: '20px',
                }}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: palette.textPrimary,
                      borderColor: enableDarkTheme
                        ? ThemePalette.typography.grey
                        : palette.borderGrey,
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${ThemePalette.typography.primary} !important`,
                      color: 'white !important',
                    },
                  }}
                />
              </Stack>
            )}
          </>
        )}
      </Box>

      {/* View Inspections Modal */}
      {isViewModalOpen && selectedInstallation && (
        <ViewInspectionsModal
          open={isViewModalOpen}
          onClose={handleCloseViewModal}
          installation={selectedInstallation}
        />
      )}

      {/* Create Inspection Modal */}
      {isCreateModalOpen && selectedInstallation && (
        <CreateInspectionModal
          open={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          installation={selectedInstallation}
          onSuccess={handleCreateSuccess}
        />
      )}
    </CustomMuiThemeProvider>
  );
};

export default TireInspection;
